import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ScriptText from "@/components/ui/ScriptText";

gsap.registerPlugin(ScrollTrigger);

interface IncludedItem {
  title: string;
  description?: string;
  note?: string;
}

interface IncludedCategory {
  label: string;
  intro?: string;
  items?: IncludedItem[];
  mayInclude?: string[];
  closing?: string;
}

interface RetreatDetailsProps {
  scriptAccent?: string;
  heading: string;
  title?: string;
  season?: string;
  location?: string;
  duration?: string;
  includedCategories: IncludedCategory[];
  closingNote?: string;
  notIncluded: string[];
  aLaCarte?: string;
}

export default function RetreatDetails({
  scriptAccent,
  heading,
  title,
  season,
  location,
  duration,
  includedCategories,
  closingNote,
  notIncluded,
  aLaCarte,
}: RetreatDetailsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const showCategoryLabels = includedCategories.length > 1;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    const reveals = el.querySelectorAll(".reveal");
    reveals.forEach((node) => {
      gsap.fromTo(
        node,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: node,
            start: "top 88%",
            toggleActions: "play none none none",
          },
        },
      );
    });

    const rows = el.querySelectorAll(".detail-row");
    rows.forEach((row, i) => {
      gsap.fromTo(
        row,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power2.out",
          scrollTrigger: {
            trigger: row,
            start: "top 90%",
            toggleActions: "play none none none",
          },
          delay: i * 0.04,
        },
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        const trigger = st.trigger;
        if (trigger && el.contains(trigger)) st.kill();
      });
    };
  }, []);

  return (
    <section ref={ref} id="whats-included" className="bg-antique-linen px-6 py-28 lg:px-12 lg:py-44">
      <div className="mx-auto max-w-3xl text-center">

        {/* Menu Header */}
        <div className="reveal mb-12">
          {scriptAccent && (
            <ScriptText className="mb-3 block text-3xl lg:text-4xl">
              {scriptAccent}
            </ScriptText>
          )}
          <h2 className="font-serif font-light tracking-wide text-soft-charcoal">
            {heading}
          </h2>
        </div>

        {/* Title & Details Cartouche */}
        <div className="reveal flex flex-col items-center gap-3">
          {title && (
            <p className="font-serif text-xl font-light italic tracking-wide text-soft-charcoal/80 lg:text-2xl">
              {title}
            </p>
          )}
          {(season || location) && (
            <p className="text-sm tracking-[0.25em] uppercase text-warm-stone">
              {season}{season && location && <>&nbsp;&middot;&nbsp;</>}{location}
            </p>
          )}
          {duration && (
            <p className="font-body text-sm tracking-[0.15em] uppercase text-soft-charcoal/60 lg:text-base">
              {duration}
            </p>
          )}
        </div>

        {/* The Menu — categorized inclusions */}
        <div className="mt-16">
          {includedCategories.map((category, ci) => (
            <div key={ci} className={ci > 0 ? "mt-16 lg:mt-20" : ""}>
              {showCategoryLabels && (
                <div className="reveal mb-8 flex flex-col items-center gap-3">
                  <span className="h-px w-10 bg-muted-gold/30" />
                  <ScriptText className="block text-2xl lg:text-3xl">
                    {category.label}
                  </ScriptText>
                </div>
              )}
              {category.intro && (
                <p className="reveal mx-auto mb-7 max-w-xl font-serif text-lg font-light italic leading-relaxed text-soft-charcoal/80 lg:text-xl">
                  {category.intro}
                </p>
              )}
              {category.items && category.items.length > 0 && (
                <div className="space-y-7">
                  {category.items.map((item, ii) => (
                    <div key={ii} className="detail-row">
                      <p className="font-serif text-lg font-light leading-relaxed tracking-wide text-soft-charcoal lg:text-xl">
                        {item.title}
                      </p>
                      {item.description && (
                        <p className="mt-1.5 font-body text-sm italic leading-relaxed text-soft-charcoal/55 lg:text-base">
                          {item.description}
                        </p>
                      )}
                      {item.note && (
                        <p className="mt-1.5 font-body text-xs italic text-warm-stone/60 lg:text-sm">
                          *{item.note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {category.mayInclude && category.mayInclude.length > 0 && (
                <div className={category.intro || (category.items && category.items.length > 0) ? "mt-7" : ""}>
                  <p className="reveal mb-5 font-body text-xs tracking-[0.25em] uppercase text-warm-stone/70 lg:text-sm">
                    Retreat may include
                  </p>
                  <ul className="space-y-3">
                    {category.mayInclude.map((entry, mi) => (
                      <li
                        key={mi}
                        className="detail-row font-serif text-base font-light leading-relaxed tracking-wide text-soft-charcoal/85 lg:text-lg"
                      >
                        {entry}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {category.closing && (
                <p className="reveal mx-auto mt-7 max-w-xl font-body text-sm italic leading-relaxed text-soft-charcoal/55 lg:text-base">
                  {category.closing}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Closing Note */}
        {closingNote && (
          <div className="reveal mt-16 flex flex-col items-center gap-4">
            <span className="h-px w-10 bg-muted-gold/30" />
            <p className="max-w-xl font-body text-sm italic leading-relaxed text-soft-charcoal/50 lg:text-base">
              {closingNote}
            </p>
          </div>
        )}

        {/* Not Included — fine print */}
        {notIncluded.length > 0 && (
          <div className="mt-20 lg:mt-28">
            <div className="reveal mb-8 flex flex-col items-center gap-4">
              <span className="h-px w-12 bg-muted-gold/20" />
              <h3 className="font-serif text-lg font-light tracking-wide text-soft-charcoal/70 lg:text-xl">
                Not Included
              </h3>
            </div>
            <div className="space-y-2.5">
              {notIncluded.map((item, i) => (
                <p
                  key={i}
                  className="detail-row font-body text-sm leading-relaxed text-soft-charcoal/45"
                >
                  {item}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* À la carte note */}
        {aLaCarte && !closingNote && (
          <p className="reveal mt-12 font-body text-sm italic text-soft-charcoal/50">
            {aLaCarte}
          </p>
        )}

      </div>
    </section>
  );
}
